import { useMemo } from "react"

import { useAppPromptsQuery } from "~graphql/app-prompts.generated"
import {
    getHighestPriorityPrompt,
    isBlockingPrompt,
} from "~components/shared/app-prompts/app-prompt-helpers"
import useAuthenticatedOutfitter from "~components/shared/app-prompts/use-authenticated-outfitter"
import useAppPromptDismissal from "~components/shared/app-prompts/use-app-prompt-dismissal"

const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000

type Options = {
    readonly pollInterval?: number
}

export default function useAppPrompts(options?: Options) {
    const pollMs = options?.pollInterval ?? DEFAULT_POLL_INTERVAL_MS
    const { isReady, outfitterId } = useAuthenticatedOutfitter()
    const { isDismissed, dismiss } = useAppPromptDismissal()

    const { data, loading, error } = useAppPromptsQuery({
        variables: { outfitterId },
        skip: !isReady,
        pollInterval: isReady ? pollMs : 0,
        fetchPolicy: "network-only",
    })

    const activePrompt = useMemo(() => {
        const prompts = data?.appPrompts ?? []

        const undismissed = prompts.filter(
            (prompt) => isBlockingPrompt(prompt) || !isDismissed(prompt.id)
        )

        return getHighestPriorityPrompt(undismissed)
    }, [data?.appPrompts, isDismissed])

    const isBlocking = activePrompt ? isBlockingPrompt(activePrompt) : false

    return {
        activePrompt,
        isBlocking,
        loading,
        error,
        dismiss,
    }
}
